import { onBeforeUnmount, onMounted, ref, type Ref } from "vue";

type StickyPosition = "top" | "bottom";

export function useIsStuck(
  target: Ref<HTMLElement | null>,
  options?: {
    position?: StickyPosition;
    root?: Element | null;
  }
) {
  const position = options?.position ?? "top";
  const isStuck = ref(position === "bottom");

  let observer: IntersectionObserver | null = null;
  let sentinel: HTMLElement | null = null;

  onMounted(() => {
    const el = target.value;
    if (!el || !el.parentNode) return;

    sentinel = document.createElement("div");
    sentinel.style.position = "absolute";
    sentinel.style.width = "100%";
    sentinel.style.height = "1px";
    sentinel.style.pointerEvents = "none";
    sentinel.setAttribute("aria-hidden", "true");

    // Ensure positioning context
    const parent = el.parentElement!;
    if (getComputedStyle(parent).position === "static") {
      parent.style.position = "relative";
    }

    if (position === "top") {
      sentinel.style.top = "0";
      parent.insertBefore(sentinel, el);
    } else {
      sentinel.style.bottom = "0";
      parent.insertBefore(sentinel, el);
    }

    // Compute rootMargin from the sentinel's actual viewport position so the
    // observer fires as soon as content scrolls behind the sticky element,
    // regardless of parent margins or safe-area insets.
    // Subtract 1px so the sentinel always starts 1px inside the observation
    // zone, guaranteeing the initial IntersectionObserver callback fires with
    // isIntersecting=true (→ isInitialized=true). Without this, a sentinel
    // exactly on the boundary may silently skip the initial callback.
    const rect = sentinel.getBoundingClientRect();
    let rootMargin: string;
    if (position === "top") {
      rootMargin = `-${Math.max(0, rect.top - 1)}px 0px 0px 0px`;
    } else {
      const distFromBottom = window.innerHeight - rect.bottom;
      rootMargin = `0px 0px -${Math.max(0, distFromBottom - 1)}px 0px`;
    }

    observer = new IntersectionObserver(
      ([entry]) => {
        isStuck.value = !entry.isIntersecting;
      },
      {
        root: options?.root ?? null,
        threshold: [0],
        rootMargin,
      }
    );

    observer.observe(sentinel);
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    sentinel?.remove();
  });

  return { isStuck };
}
